import { isPlatformBrowser } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, effect, ElementRef, inject, OnDestroy, OnInit, PLATFORM_ID, signal, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Apollo, gql } from 'apollo-angular';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { AssetFragment, GetActiveOrderQuery } from 'src/app/common/generated-types';
import { GET_PRODUCTS } from 'src/app/common/graphql/documents.graphql';
import { AssistantComponentImports } from '../..';
import { GET_ACTIVE_ORDER } from '../../providers/active/active.service.graphql';
import { ActiveService } from '../../providers/active/active.service';
import { DataService } from '../../providers/data/data.service';
import { StateService } from '../../providers/state/state.service';
import { WishlistService } from '../../providers/wishlist/wishlist.service';

enum MessageType {
  Text = 'text',
  Items = 'items',
  Confirm = 'confirm',
}

interface Message {
  text: string;
  timestamp: Date;
  isMine: boolean;
  type?: MessageType;
  productCards?: ProductCard[];
  confirm?: boolean;
  role?: string;
  content?: string;
}

interface ProductCard {
  id: string;
  name: string;
  price: number;
  imageUrl?: AssetFragment;
  slug: string;
}

type SendAssistantMessageMutation = {
  aiAgentChat: any;
};

const SEND_ASSISTANT_MESSAGE = gql`
    mutation ($messages: [AiMessageInput!]!) {
            aiAgentChat(messages: $messages) {
              messages {
                role
                content
              }
              productCards {
                id
                name
                price
                imageUrl {
                  id
                  preview
                  width
                  height
                  focalPoint {
                    x
                    y
                  }
                }
                slug
              }
              redirectUrl
            }
          }
`;

@Component({
  standalone: true,
  selector: 'vsf-assistant',
  imports: AssistantComponentImports,
  templateUrl: './assistant.component.html',
  styleUrl: './assistant.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssistantComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly STORAGE_KEY = 'ai_assistant_messages';
  private apollo = inject(Apollo);
  private cdr = inject(ChangeDetectorRef);
  private router = inject(Router);
  private dataService = inject(DataService);
  private stateService = inject(StateService);
  private activeService = inject(ActiveService);
  private wishlistService = inject(WishlistService);
  private platformId = inject(PLATFORM_ID);
  

  @ViewChild('messagesContainer', { static: false })
  private messagesContainer!: ElementRef<HTMLDivElement>;
  private observer?: MutationObserver;
  readonly isBrowser = isPlatformBrowser(this.platformId);
  
  isOpen = signal(false);
  isFullscreen = signal(false);
  isTyping = signal(false);
  messageText = '';
  messages = signal<Message[]>([]);
  product: ProductCard | null = null;
  signedIn$!: Observable<boolean>;
  showLoginTooltip = signal(false);

  constructor() {
    effect(() => {
      if (!this.isBrowser) {
        return;
      }

      const msgs = this.messages();
      if (msgs.length > 0) {
        this.saveMessages(msgs);
        this.scrollToBottom();
      }
    });
  }

  ngOnInit() {
    this.signedIn$ = this.stateService.select(state => state.signedIn);

    if (!this.isBrowser) {
      return;
    }

    this.loadMessages();
    this.loadProduct();
  }

  ngAfterViewInit() {
    if (!this.isBrowser) {
      return;
    }

    const el = this.messagesContainer?.nativeElement;
    if (!el) return;

    this.observer = new MutationObserver(() => {
      this.scrollToBottom();
    });

    this.observer.observe(el, {
      childList: true,
      subtree: true,
    });
  }

  toggleChat() {
    this.signedIn$.pipe(
      take(1),
      map(signedIn => {
        if (signedIn) {
          this.isOpen.update(value => !value);
          this.showLoginTooltip.set(false);
        } else {
          this.showLoginTooltip.set(true);
          setTimeout(() => {
            this.showLoginTooltip.set(false);
            this.cdr.markForCheck();
          }, 3000);
        }
        this.cdr.markForCheck();
      })
    ).subscribe();
  }

  closeChat() {
    this.isOpen.set(false);
    this.isFullscreen.set(false);
  }

  toggleFullscreen() {
    this.isFullscreen.update(value => !value);
  }

  private scrollToBottom(): void {
    const el = this.messagesContainer?.nativeElement;
    if (!el) return;

    queueMicrotask(() => {
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
      });
    });
  }

  private isUserNearBottom(): boolean {
    const el = this.messagesContainer?.nativeElement;
    if (!el) return true;

    return el.scrollHeight - el.scrollTop - el.clientHeight < 40;
  }

  private scrollToBottomIfNeeded() {
    if (this.isUserNearBottom()) {
      this.scrollToBottom();
    }
  }

  sendMessage() {
    this.scrollToBottom();
    if (this.messageText.trim()) {
      const userMessage: Message = {
        text: this.messageText.trim(),
        timestamp: new Date(),
        isMine: true,
      };

      this.messages.update(msgs => [...msgs, userMessage]);
      this.scrollToBottomIfNeeded();
      
      this.isTyping.set(true);
      
      this.apollo.mutate({
        mutation: SEND_ASSISTANT_MESSAGE,
        variables: { messages: this.messages().map(msg => {
          return { role: msg.isMine ? 'user' : 'assistant', content: msg.text };
        }) }
      }).subscribe({
      next: (result: any) => {
        const aiAgentChat = result.data?.aiAgentChat;
        const redirectUrl = aiAgentChat?.redirectUrl;

        this.messages.set(aiAgentChat.messages.map((msg: any) => ({
          text: msg.role === 'assistant' ? this.normalizeAssistantText(msg.content) : msg.content,
          timestamp: new Date(),
          isMine: msg.role === 'user',
          productCards: msg.role === 'user' ? [] : aiAgentChat.productCards || [],
        })));

        this.isTyping.set(false);
        this.refreshStorefrontState();
        this.cdr.markForCheck();

        if (redirectUrl) {
          this.router.navigateByUrl(redirectUrl);
        }
      },
      error: (error) => {
        console.error('Error sending message:', error);
        this.isTyping.set(false);
        this.cdr.markForCheck();
      }
    });
    this.messageText = '';
  }
}

  private loadProduct() {
    this.apollo.query({
      query: GET_PRODUCTS,
      variables: { take: 1, skip: 0 }
    })
    .pipe(take(1))
    .subscribe((result: any) => {
      if (result.search && result.search.products.items.length > 0) {
        const product = result.search.products.items[0];
        this.product = {
          id: product.id,
          name: product.name,
          price: product.variants[0].priceWithTax / 100,
          imageUrl: product.featuredAsset?.preview || '',
          slug: product.slug
        };
        this.cdr.markForCheck();
      }
    });
  }

  private saveMessages(messages: Message[]) {
    try {
      const messagesToSave = messages.map(msg => ({
        text: msg.text,
        timestamp: msg.timestamp,
        isMine: msg.isMine,
        productCards: msg.productCards
      }));
      const messagesJson = JSON.stringify(messagesToSave);
      localStorage.setItem(this.STORAGE_KEY, messagesJson);
    } catch (error) {
      console.error('Помилка збереження повідомлень:', error);
    }
  }

  private loadMessages() {
    try {
      const messagesJson = localStorage.getItem(this.STORAGE_KEY);
      if (messagesJson) {
        const loadedMessages = JSON.parse(messagesJson);
        const messages = loadedMessages.map((msg: any) => ({
          ...msg,
          text: msg.isMine ? msg.text : this.normalizeAssistantText(msg.text),
          timestamp: new Date(msg.timestamp)
        }));
        this.messages.set(messages);
      }
    } catch (error) {
      // console.error('Помилка завантаження повідомлень:', error);
    }
  }

  clearHistory() {
    this.messages.set([]);
    localStorage.removeItem(this.STORAGE_KEY);
  }

  private refreshStorefrontState() {
    this.dataService
      .query<GetActiveOrderQuery>(GET_ACTIVE_ORDER, {}, 'network-only')
      .pipe(take(1))
      .subscribe({
        next: data => {
          if (data.activeOrder) {
            this.activeService.setActiveOrder(data.activeOrder);
          } else {
            this.activeService.clearActiveOrder();
          }
          this.cdr.markForCheck();
        },
      });

    this.wishlistService.refreshWishlists().pipe(take(1)).subscribe({
      next: () => this.cdr.markForCheck(),
    });
  }

  navigateToProduct(slug: string) {
    this.router.navigate(['/product', slug]);
  }

  private normalizeAssistantText(text: string): string {
    return String(text ?? '')
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/__(.*?)__/g, '$1')
      .replace(/`([^`]+)`/g, '$1')
      .replace(/^#{1,6}\s+/gm, '')
      .replace(/^\s*[-*]\s+/gm, '')
      .trim();
  }

  ngOnDestroy() {
    this.observer?.disconnect();
  }
}
